import styled from 'styled-components';
import moment from 'moment';
import { Box, BoxProps, Flex } from 'reflexbox';
import { rgba } from 'polished';
import { useTranslation } from 'react-i18next';

import { Select } from './Select';
import { Label } from './Text';
import { Skeleton } from './Skeleton';

import { ChevronLeftIcon, ChevronRightIcon } from '@treams/treams-icons';
import { useQueryParameters } from '@treams/treams-hooks';
import { ComponentProps, useEffect } from 'react';
import { useHistory } from '@treams/treams-routes';
import { Input } from './Input';

export type TimeInterval = 'month' | 'quarter' | 'year' | 'custom';

export interface ITimeMachine {
	current: [Date, Date];
	previous?: Date;
	next?: Date;
	interval: TimeInterval;
	urlCursor: string;
}

export function createTimeMachineFromQuery() {
	// eslint-disable-next-line react-hooks/rules-of-hooks
	const queryParameters = useQueryParameters(['cursor', 'interval', 'end']);
	const interval = queryParameters.interval as TimeInterval;

	if (interval === 'custom') {
		return createCustomTimeMachine(
			moment(queryParameters.cursor).toDate(),
			moment(queryParameters.end).toDate(),
		);
	}

	return createTimeMachine(
		interval ?? 'month',
		queryParameters.cursor ? moment(queryParameters.cursor).toDate() : moment().toDate(),
	);
}

export function createCustomTimeMachine(cursor: Date, end: Date): ITimeMachine {
	const current: [Date, Date] = [
		moment(cursor).startOf('day').toDate(),
		moment(end).startOf('day').toDate(),
	];

	return {
		current,
		previous: moment(current[0])
			.subtract(moment(current[1]).diff(moment(current[0])), 'ms')
			.toDate(),
		next: undefined,
		interval: 'custom',
		urlCursor: current.map((date) => moment(date).format('YYYY-MM-DD')).join('&end='),
	};
}

export function createTimeMachine(
	interval: Exclude<TimeInterval, 'custom'>,
	cursor: Date,
	workspaceStartDate?: Date,
	allowFutureDates?: boolean,
): ITimeMachine {
	const current: [Date, Date] = [
		moment(cursor).startOf(interval).toDate(),
		moment(cursor).endOf(interval).toDate(),
	];
	const previous = moment(cursor).startOf(interval).subtract(1, interval).toDate();
	const next = moment(cursor).startOf(interval).add(1, interval).toDate();

	return {
		current,
		previous:
			!workspaceStartDate || moment(current[0]).isAfter(moment(workspaceStartDate))
				? previous
				: undefined,
		next: moment(current[1]).isAfter(moment()) && !allowFutureDates ? undefined : next,
		interval,
		urlCursor: moment(current[0]).format('YYYY-MM-DD'),
	};
}

const DatePeriodNavigationElement = styled(Flex)`
	border-radius: 5px;
	background: ${(props) => props.theme.colors.white};
	box-shadow:
		0px 2px 4px ${(props) => rgba(props.theme.colors.slateBlue, 0.1)},
		0px 1px 2px ${(props) => rgba(props.theme.colors.slateBlue, 0.05)},
		0px 0px 0px 1px ${(props) => rgba(props.theme.colors.slateBlue, 0.25)} inset;

	${Label} {
		color: ${(props) => props.theme.colors.antracite};
	}
`;

const DatePeriodNavigationButton = styled.button`
	height: 40px;
	width: 34px;
	border: 0;
	outline: 0;
	background: transparent;
	display: block;
	padding: 0;
	margin: 0;

	:first-child {
		border-right: 1px solid ${(props) => props.theme.colors.lightBlue};
	}

	:last-child {
		border-left: 1px solid ${(props) => props.theme.colors.lightBlue};
	}

	svg {
		width: 24px;

		path {
			fill: ${(props) => props.theme.colors.slateBlue};
		}
	}

	:disabled {
		pointer-events: none;
		svg {
			opacity: 0.3;
		}
	}
`;

export function DateIntervalValue(props: { value: ITimeMachine }) {
	return (
		<>
			{props.value.interval === 'month' && moment(props.value.current[0]).format('MMMM YYYY')}
			{props.value.interval === 'quarter' && `Q${moment(props.value.current[0]).format('Q YYYY')}`}
			{props.value.interval === 'year' && moment(props.value.current[0]).format('YYYY')}
		</>
	);
}

export type DateIntervalNavigationProps = BoxProps & {
	value: ITimeMachine;
	onChange: (timeMachine: ITimeMachine) => void;
	workspaceStartDate?: Date;
	disableQuery?: boolean;
	allowFutureDates?: boolean;
};

export function DateIntervalNavigation({ onChange, ...props }: DateIntervalNavigationProps) {
	const history = useHistory();

	useEffect(() => {
		if (!props.disableQuery) {
			history.push(
				`${history.location.pathname}?interval=${props.value.interval}&cursor=${props.value.urlCursor}`,
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.value]);

	const { t } = useTranslation();

	return (
		<Flex {...(props as ComponentProps<typeof Box>)}>
			<Flex mr={2}>
				<Select
					options={[
						{ title: t('dateInterval.interval.month'), value: 'month' },
						{ title: t('dateInterval.interval.quarter'), value: 'quarter' },
						{ title: t('dateInterval.interval.year'), value: 'year' },
						undefined,
						{ title: t('dateInterval.interval.custom'), value: 'custom' },
					]}
					selectedValue={props.value.interval}
					search={false}
					onChange={(interval: TimeInterval) => {
						if (interval === 'custom') {
							onChange(createCustomTimeMachine(...props.value.current));
						} else {
							onChange(
								createTimeMachine(
									interval,
									moment().toDate(),
									props.workspaceStartDate,
									props.allowFutureDates,
								),
							);
						}
					}}
				/>
			</Flex>

			{props.value.interval === 'custom' ? (
				<>
					<Box mr={2} width={140}>
						<Input.DatePicker
							value={props.value.current[0]}
							onChange={(date) => {
								onChange(createCustomTimeMachine(date!, props.value.current[1]));
							}}
						/>
					</Box>
					<Box width={140}>
						<Input.DatePicker
							value={props.value.current[1]}
							onChange={(date) => {
								onChange(createCustomTimeMachine(props.value.current[0], date!));
							}}
						/>
					</Box>
				</>
			) : (
				<DatePeriodNavigationElement alignItems="center">
					<DatePeriodNavigationButton
						disabled={props.value.previous === undefined}
						onClick={() => {
							if (props.value.interval === 'custom') {
								return;
							}

							onChange(
								createTimeMachine(
									props.value.interval,
									props.value.previous!,
									props.workspaceStartDate,
									props.allowFutureDates,
								),
							);
						}}
					>
						<ChevronLeftIcon />
					</DatePeriodNavigationButton>
					<Label px={3}>
						<DateIntervalValue value={props.value} />
					</Label>
					<DatePeriodNavigationButton
						disabled={props.value.next === undefined}
						onClick={() => {
							if (props.value.interval === 'custom') {
								return;
							}

							onChange(
								createTimeMachine(
									props.value.interval,
									props.value.next!,
									props.workspaceStartDate,
									props.allowFutureDates,
								),
							);
						}}
					>
						<ChevronRightIcon />
					</DatePeriodNavigationButton>
				</DatePeriodNavigationElement>
			)}
		</Flex>
	);
}

DateIntervalNavigation.Skeleton = styled(Skeleton.Box)`
	height: 40px;
	width: 200px;
`;
